import { Suspense, useEffect, useRef, useState } from "react";
import { useScrollLock } from "../../utils/hooks/useScrollLock";
import { LoadingSplash } from "./LoadingSplash";
import { MountNotifierProps, Props } from "./SuspenseWithLoadingSplash.Parts";

export const SuspenseWithLoadingSplash = ({
  suspenseKey,
  children,
  awaitAnimation = true,
}: Props) => {
  const [suspenseReady, setSuspenseReady] = useState(false);
  const [animationCompleted, setAnimationCompleted] = useState(!awaitAnimation);
  const [showSplash, setShowSplash] = useState(false);
  const suspenseReadyRef = useRef(suspenseReady);

  const overlayVisible = !suspenseReady || !animationCompleted;

  useEffect(() => {
    suspenseReadyRef.current = suspenseReady;
  }, [suspenseReady]);

  useScrollLock(overlayVisible && showSplash);

  /**@todo Remove when AuxClients is retired */
  const isFromPlayer = document.referrer
    ? new URL(document.referrer).hostname === "player.studio.massivemusic.com"
    : false;

  useEffect(() => {
    setShowSplash(false);
    setSuspenseReady(false);
    setAnimationCompleted(!awaitAnimation);

    if (isFromPlayer) {
      const timer = setTimeout(() => {
        if (!suspenseReadyRef.current) {
          setShowSplash(true);
          setAnimationCompleted(true);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setShowSplash(true);
    }
  }, [suspenseKey, awaitAnimation, isFromPlayer]);

  return (
    <>
      {overlayVisible && showSplash && (
        <LoadingSplash
          delayAnimation={isFromPlayer ? 150 : undefined}
          overlay
          onComplete={() => setAnimationCompleted(true)}
        />
      )}
      <Suspense key={suspenseKey} fallback={null}>
        {children}
        <MountNotifier onMounted={() => setSuspenseReady(true)} />
      </Suspense>
    </>
  );
};

const MountNotifier = ({ onMounted }: MountNotifierProps) => {
  useEffect(() => {
    onMounted?.();
  }, [onMounted]);
  return null;
};
